<template>
  <div>
    <div
      v-if="lastConversation"
      class="tw-bg-white tw-rounded-lg tw-p-6 tw-mb-6"
    >
      <Typography variant="headline-6" class="tw-pb-2"
        >Last conversation</Typography
      >
      <div>
        <div
          class="tw-mt-3 tw-border-0 tw-border-t tw-border-solid tw-border-gray-300 tw-pt-3"
        >
          <div
            class="tw-cursor-pointer tw-flex tw-justify-between tw-items-center tw-gap-2 tw-relative"
          >
            <div class="tw-flex tw-items-center tw-gap-2">
              <div>
                <div class="tw-w-[24px] tw-h-[24px]">
                  <CustomImage
                    :src="profilePicture(lastConversation)"
                    class="tw-w-full tw-h-full tw-object-cover"
                    :emptySrc="require('@/assets/img/empty_avatar.png')"
                    width="24"
                    height="24"
                  />
                </div>
              </div>
              <div class="tw-break-words tw-max-w-[140px]">
                <Typography variant="subtitle-2">
                  {{ lastConversationName }}
                </Typography>
              </div>
            </div>
            <div>
              <Typography variant="caption" class="tw-text-gray-700">{{
                formatDate(lastConversation.date)
              }}</Typography>
            </div>
          </div>
          <Typography
            variant="body-2"
            class="tw-py-3 tw-whitespace-break-spaces w-full"
          >
            <div
              class="whitespace-pre-line"
              v-html="removeTrackingCode(lastConversation.email_body)"
            ></div>
          </Typography>
        </div>
      </div>
      <div>
        <Button
          variant="outlined"
          size="small"
          class="tw-w-full"
          @click="$emit('expand-email')"
        >
          <div class="tw-flex tw-items-center tw-gap-2 tw-justify-center">
            <div>
              <img :src="ExpandIcon" />
            </div>
            <div>Full conversation</div>
          </div>
        </Button>
      </div>
    </div>
    <div class="tw-bg-white tw-rounded-lg tw-p-6">
      <Typography variant="headline-6" class="tw-pb-2"
        >Reasons to hire</Typography
      >
      <div
        v-for="(reason, index) in candidate.reasons_to_hire
          ? candidate.reasons_to_hire.slice(0, 3)
          : []"
        v-bind:key="index"
        class="tw-flex tw-gap-2 tw-items-center tw-py-2"
      >
        <img v-bind:src="getIcon(reason)" />
        <Typography variant="body-2">
          {{ getText(reason) }}
        </Typography>
      </div>
    </div>
    <div v-if="isHireflyAdmin" class="tw-bg-white tw-rounded-lg tw-p-6 tw-mt-6">
      <Typography variant="headline-6" class="tw-pb-2">
        Notes from Hirefly
      </Typography>
      <Typography variant="body-2" class="tw-pb-2">
        Notes will be added to the candidate’s card for the project owner to
        review.
      </Typography>
      <Typography
        variant="body-2"
        class="tw-border tw-border-solid tw-border-gray-400 tw-rounded-lg tw-overflow-hidden tw-p-3"
      >
        <v-textarea
          v-model="noteText"
          class="note-box"
          hide-details=""
          solo
          height="68px"
          light
          placeholder="Your notes..."
          @change="sendNote"
        >
        </v-textarea>
      </Typography>
    </div>
    <HardRequirements
      v-if="(candidate.notes || displayClipBoard) && !loadingTab"
      :projectData="projectData"
      :notes="candidate.notes"
    />
    <div
      v-if="tab === 'REJECTED'"
      class="tw-bg-white tw-rounded-lg tw-p-6 tw-mt-6"
    >
      <Typography variant="headline-6" class="tw-pb-2"
        >Rejection Details</Typography
      >
      <Typography variant="body-2">
        <div
          v-if="
            candidate.rejection_reason_formatted &&
            candidate.rejection_reason_formatted[0] !==
              candidate.rejection_reason_other
          "
        >
          {{
            `${candidate.rejection_reason_formatted[0]}
          ${
            candidate.rejection_reason_other
              ? ': ' + candidate.rejection_reason_other
              : ''
          }`
          }}
        </div>
        <div
          v-if="
            candidate.rejection_reason_formatted.length &&
            candidate.rejection_reason_formatted[0] ===
              candidate.rejection_reason_other
          "
          class="tw-mt-2"
        >
          {{ candidate.rejection_reason_formatted[0] }}
        </div>
      </Typography>
    </div>
  </div>
</template>

<script>
import { format } from 'date-fns';
import axios from 'axios';
import Config from '@/js/config';
import Typography from '@/components/v3/base/Typography';
import CustomImage from '@/components/card/CustomImage';
import Button from '@/components/v3/base/Button';
import ExpandIcon from '@/assets/icons/expand-03.svg';
import HardRequirements from './HardRequirements';

const BRIEF_CASE = require('../../assets/img/suitcase.svg');
const GRADUATION = require('../../assets/img/graduation-hat.svg');
const DIAMOND = require('../../assets/img/diamond.svg');
const CHECKMARK = require('../../assets/img/checkmark.png');

export default {
  name: 'reasons',
  components: {
    Typography,
    HardRequirements,
    CustomImage,
    Button,
  },

  props: [
    'candidate',
    'tab',
    'projectId',
    'notes',
    'displayClipBoard',
    'projectData',
    'loadingTab',
  ],

  data() {
    return {
      panel: 0,
      noteText: '',
      ExpandIcon,
    };
  },

  computed: {
    // a computed getter
    totalCompensation() {
      if (
        this.candidate.estimated_salary &&
        this.candidate.estimated_salary.length === 2
      ) {
        const salary = this.candidate.estimated_salary;
        return `$${this.formatMoney(salary[0])}-$${this.formatMoney(
          salary[1]
        )}`;
      }
      return undefined;
    },

    lastConversation() {
      if (
        this.candidate.email_history &&
        this.candidate.email_history.length > 0
      ) {
        return this.candidate.email_history[
          this.candidate.email_history.length - 1
        ];
      }
      return undefined;
    },

    candidateEmails() {
      const personalEmails = this.candidate.personal_emails ?? [];
      const profEmails = this.candidate.prof_emails ?? [];
      return [...personalEmails, ...profEmails];
    },

    user() {
      return this.$store.getters['user/getUser'];
    },

    lastConversationName() {
      if (this.candidateEmails.includes(this.lastConversation.from_email)) {
        const name = this.candidate.name.split(' ');
        if (name.length < 3) {
          return name[0];
        }
        return this.candidate.name;
      }
      if (this.user.email === this.lastConversation.from_email) {
        return this.user.first_name;
      }
      return this.lastConversation.from_email;
    },

    isHireflyAdmin() {
      const email = this.user.email;
      if (
        email &&
        this.getEmailDomain(email) &&
        ['hirefly.ai', 'usetango.ai', 'hirefly-recruiting.com'].some(
          (item) => item === this.getEmailDomain(email)
        )
      ) {
        return true;
      }
      return false;
    },
  },

  watch: {
    notes(v) {
      if (v) {
        this.noteText = v;
      } else {
        this.noteText = '';
      }
    },
  },

  created() {
    if (this.notes) {
      this.noteText = this.notes;
    } else {
      this.noteText = '';
    }
  },

  methods: {
    sendNote() {
      axios.defaults.headers.put['Content-Type'] =
        'application/x-www-form-urlencoded';
      const session = localStorage.getItem('session');
      axios.defaults.headers.put['X-Parse-Session-Token'] = session;
      axios.put(`${Config.env.API_URL}candidates`, {
        candidate_id: this.candidate.id,
        note_to_hm: this.noteText,
        project_id: this.projectId,
      });
    },

    formatMoney(money) {
      return `${(money / 1000).toFixed(0)}K`;
    },

    getIcon(reason) {
      if (reason.Work) {
        return BRIEF_CASE;
      }
      if (reason.Education) {
        return GRADUATION;
      }
      if (reason.Boost || reason.Skill) {
        return DIAMOND;
      }
      return CHECKMARK;
    },

    getText(reason) {
      if (typeof reason === 'string' || reason instanceof String) {
        return reason;
      }

      const keys = Object.keys(reason);
      if (keys && keys.length > 0) {
        return reason[keys[0]];
      }
      return '';
    },

    profilePicture(history) {
      if (!history) return '';

      if (this.candidate?.personal_emails?.includes(history.from_email)) {
        return this.candidate.profile_pic_url ?? this.candidate.picture_url;
      }

      return `https://ui-avatars.com/api/?background=ffffff&color=384955&name=${history.from_email}`;
    },

    removeTrackingCode(str) {
      let text = str;
      if (text && text.includes('[tracking]')) {
        const ind = text.indexOf('[ Click here to unsubscribe');
        if (ind > 0) text = text.substring(0, ind);
        const ind3 = text.indexOf('[Click here to unsubscribe');
        if (ind3 > 0) text = text.substring(0, ind3);
        const ind2 = text.indexOf('![tracking]');
        if (ind2 > 0) text = text.substring(0, ind2);
      }
      return text;
    },

    replaceWithLinks(str) {
      if (!str) return '';
      let text = this.removeTrackingCode(str);
      const urlRegex = /\((https?:\/\/[^\s]+)/g;
      if (text.match(urlRegex)) {
        // eslint-disable-next-line no-unreachable-loop, guard-for-in
        for (const i in text.match(urlRegex)) {
          text = text.replace(
            /\[(.+?)\]/g,
            `<a target="_blank" style="color: #111827; font-weight: 600" href="${
              (text.replace(urlRegex),
              text.match(urlRegex)[i].replaceAll(/\(+(.*?)\)+/g, '$1'))
            }">$1</a>`
          );
          // eslint-disable-next-line no-useless-escape
          text = text.replace(/\>\((https?:\/\/[^\s]+)/g, '>');
          return text;
        }
      }
      return text;
    },

    formatDate(val) {
      if (!val) return '';
      return format(val, 'PP');
    },

    getEmailDomain(email) {
      if (!email.includes('@')) return null; // Validate the format
      return email.split('@')[1];
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/styles/variables.scss';

.divider {
  height: 2px;
  background-color: #ddd;
}

.reasons-box {
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    border-radius: 2.5px;
    background-color: $scrollbar-color;
  }
}

.reasons::v-deep .v-expansion-panel {
  color: $gray-dark !important;
}

.reasons::v-deep .v-expansion-panel:before {
  box-shadow: none;
}

.reasons::v-deep .v-expansion-panel:after {
  border-color: white !important;
  opacity: 1 !important;
}

.reasons .panel::v-deep -content__wrap {
  padding-left: 0;
  padding-right: 0;
}

.reasons::v-deep .v-expansion-panel-header {
  min-height: 0;
  outline: none;
}

.reasons .panel::v-deep .v-divider {
  border-color: rgba(233, 233, 233, 0.49) !important;
  margin: 1rem 2rem;
}

.primary-reason-icon {
  height: 1.25rem;
  width: 1.25rem;
  margin-right: 10px;
}

.content-between {
  align-content: space-between;
  display: flex;
  flex-wrap: wrap;
  height: 100%;
}
.notes-box {
  // height: 110px;
  border-top: 2px solid #d8d8d8;
  padding-top: 3px !important;
  margin-right: 3px;
}
.rejection-detail {
  border-top: 2px solid #d8d8d8;
  padding-left: 12px;
  padding-right: 12px;
  font-size: 12px;
  color: $gray-dark;
}
.title-text {
  font-size: 12px;
  letter-spacing: 0.35px;
  color: $gray-dark;
  font-weight: 500;
}
.note-box {
  padding-left: 0px !important;
  &::v-deep .v-input__slot {
    box-shadow: none !important;
    padding-right: 0px !important;
    padding-left: 0px !important;
  }
  &::v-deep .v-input__slot:after {
    border: none;
  }
  &::v-deep textarea {
    resize: none;
    margin-top: -2px !important;
    line-height: 18px !important;
    color: $gray-dark !important;
    padding-right: 2px !important;
    padding-left: 0px !important;
    font-size: 14px;
  }
  &::v-deep .v-input__control {
    height: 100%;
  }
  &::v-deep .v-input__slot {
    height: 100%;
  }
}
.purple-border {
  border: solid 2px;
  color: $primary;
}
</style>
